/**
 * Single source of truth for B2C-only (personal user) routes, plus the pre-paint <head> script
 * that enforces them on hard loads. Dependency-free on purpose: the root layout (a server
 * component) imports the script builder, so this module must not pull in client-only auth code.
 * The access decision used by B2CRouteGuard lives in lib/b2cRouteAccess.ts.
 */

export const ENTERPRISE_LANDING_PATH = '/enterprise/dashboard'

/** Unknown-persona fallback: the public landing page, which renders for every session. */
export const UNKNOWN_PERSONA_FALLBACK_PATH = '/'

export type B2COnlyRoute = {
  /** Route root; the route itself and every nested segment below it are restricted. */
  pathPrefix: string
  /**
   * True for public/SEO pages whose server-rendered HTML must be kept for crawlers and
   * signed-out visitors. These render during hydration and rely on the pre-paint <head> script
   * to hide and redirect enterprise sessions on hard loads. All other routes stay unmounted
   * until the client confirms access.
   */
  preserveServerRender: boolean
}

export const B2C_ONLY_ROUTES: readonly B2COnlyRoute[] = [
  { pathPrefix: '/history', preserveServerRender: false },
  { pathPrefix: '/user-profile', preserveServerRender: false },
  { pathPrefix: '/my-transactions', preserveServerRender: false },
  { pathPrefix: '/referral', preserveServerRender: false },
  { pathPrefix: '/checkout', preserveServerRender: false },
  { pathPrefix: '/skill-assessment', preserveServerRender: false },
  { pathPrefix: '/interview-summary', preserveServerRender: false },
  { pathPrefix: '/question-review', preserveServerRender: false },
  { pathPrefix: '/mock-interview', preserveServerRender: true },
  { pathPrefix: '/system-design-interview', preserveServerRender: true },
  { pathPrefix: '/practice-questions', preserveServerRender: true },
]

/** Strips query/hash, collapses repeated slashes, and drops trailing slashes (except root). */
export function normalizeRoutePathname(pathname: string): string {
  const pathWithoutQuery = pathname.split('?')[0]?.split('#')[0] ?? ''
  const collapsedPath = pathWithoutQuery.replace(/\/{2,}/g, '/')
  const trimmedPath = collapsedPath.replace(/\/+$/, '')
  return trimmedPath === '' ? '/' : trimmedPath
}

/** Matches on segment boundaries only: `/history` and `/history/x`, never `/history-help`. */
export function findB2COnlyRoute(pathname: string | null | undefined): B2COnlyRoute | null {
  if (!pathname) return null
  const normalizedPath = normalizeRoutePathname(pathname)
  return (
    B2C_ONLY_ROUTES.find(
      (route) =>
        normalizedPath === route.pathPrefix || normalizedPath.startsWith(`${route.pathPrefix}/`),
    ) ?? null
  )
}

/** Mirrors isEnterpriseIdentitySession() in lib/auth.ts, kept here so this module stays dependency-free. */
const ENTERPRISE_PERSONA_JWT_ROLES: readonly string[] = ['enterprise', 'enterprise_owner', 'recruiter']

export function isEnterprisePersonaJwtRole(role: string | undefined | null): boolean {
  return typeof role === 'string' && ENTERPRISE_PERSONA_JWT_ROLES.includes(role)
}

/**
 * Self-contained inline script for <head>. Runs before first paint on every hard load (direct
 * URL, refresh, new tab). For an enterprise owner/recruiter token on a B2C-only path it hides the
 * document and replaces the location with the enterprise landing page, so restricted content is
 * never painted. Anything it cannot classify is left to B2CRouteGuard.
 *
 * Kept as a plain string (not a serialized function) so bundler transforms cannot inject helper
 * references into it. Tests evaluate this exact string against a fake window/document.
 */
export function buildPreHydrationB2CRouteGuardScript(): string {
  const scriptConfig = {
    routePrefixes: B2C_ONLY_ROUTES.map((route) => route.pathPrefix),
    enterprisePersonaRoles: ENTERPRISE_PERSONA_JWT_ROLES,
    destination: ENTERPRISE_LANDING_PATH,
  }
  return `(function(config){try{
var path=(window.location.pathname||'/').replace(/\\/{2,}/g,'/').replace(/\\/+$/,'')||'/';
var restricted=false;
for(var index=0;index<config.routePrefixes.length;index++){var prefix=config.routePrefixes[index];if(path===prefix||path.indexOf(prefix+'/')===0){restricted=true;break;}}
if(!restricted)return;
var token=window.localStorage.getItem('authToken');
if(!token)return;
var encodedPayload=token.split('.')[1];
if(!encodedPayload)return;
var base64=encodedPayload.replace(/-/g,'+').replace(/_/g,'/');
var json=decodeURIComponent(Array.prototype.map.call(window.atob(base64),function(character){return '%'+('00'+character.charCodeAt(0).toString(16)).slice(-2);}).join(''));
var payload=JSON.parse(json);
if(!payload||payload.identity_type==='enterprise_candidate')return;
if(config.enterprisePersonaRoles.indexOf(payload.role)===-1)return;
document.documentElement.style.visibility='hidden';
window.location.replace(config.destination);
}catch(error){/* Unclassifiable token: B2CRouteGuard still blocks the page and handles it. */}})(${JSON.stringify(scriptConfig)});`
}
